import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { useSelector, useDispatch } from 'react-redux';
import { getProductReviews, resetReviews } from '../../actions/ratings-reviews-actions';
import { Container, Row, Column } from '@pella/sash-framework';
import { Layout, SEO, Breadcrumbs, Image } from '../../components';
import ImageBanner from '../../components/common/image-banner';
import ReviewsSlider from '../../components/common/reviews-slider';
import PerformancePanel from '../../components/common/performance-panel';
import Feature from '../../components/common/feature';
import DynamicComponent from '../../components/common/dynamic-component';
import CardSection from '../../components/common/card-section';
import CTARibbon from '../../components/common/cta-ribbon';
import HeroSimple from '../../components/common/hero-simple';
import styles from './index.module.scss';

const PLLP = (props) => {
	const pageData = props.data.contentstackPllp;
	const reviews = useSelector((state) => state.reviews);
	const [ratings, setRatings] = useState({});
	const dispatch = useDispatch();

	useEffect(() => {
		// Grab Product Reviews
		if (pageData.ratings_code?.[0]?.ratings_code) {
			dispatch(getProductReviews(pageData.ratings_code[0].ratings_code));
		} else {
			// Otherwise reset reviews
			dispatch(resetReviews());
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- preexisting failure, fix when possible
	}, [pageData]);

	useEffect(() => {
		if (reviews?.[pageData?.ratings_code?.[0]?.ratings_code]) {
			setRatings(reviews);
		}
	}, [reviews, pageData]);

	return (
		<Layout
			id='template-PLLP'
			pageType={pageData.seo.page_type}
		>
			{pageData.breadcrumbs && (
				<Breadcrumbs
					data={pageData.breadcrumbs}
					currentUrl={pageData.url}
				/>
			)}

			{pageData.page_hero?.[0] && <HeroSimple data={pageData.page_hero[0]} />}

			{pageData.modular_blocks?.[0] &&
				pageData.modular_blocks.map((block, index) => {
					if (block.all_dynamic?.dynamic?.[0]) {
						return (
							<DynamicComponent
								key={index}
								data={block.all_dynamic}
							/>
						);
					}

					if (block.banner?.banner?.[0]) {
						return (
							<ImageBanner
								key={index}
								data={block.banner.banner[0]}
							/>
						);
					}

					if (block.feature?.feature?.[0]) {
						return (
							<Feature
								key={index}
								data={block.feature}
							/>
						);
					}

					if (block.performance?.performance_or_packages?.[0]) {
						return (
							<PerformancePanel
								key={index}
								data={block.performance.performance_or_packages[0]}
							/>
						);
					}

					if (block.reviews != null && ratings?.Reviews?.[0]) {
						return (
							<ReviewsSlider
								key={index}
								reviews={ratings}
								title={block.reviews.heading}
								cta={block.reviews.ctas}
								className={styles.pllp_Reviews}
							/>
						);
					}

					if (block.banner_with_feature != null) {
						return (
							<div
								className={`${styles.section_Wrapper} ${styles.performance}`}
								key={index}
							>
								<Container width={1800}>
									{block.banner_with_feature.image?.[0] && (
										<Image
											data={block.banner_with_feature.image[0]}
											className={styles.productFeatures_Image}
										/>
									)}

									{block.banner_with_feature.feature_content?.[0] && (
										<Row
											verticalGutters={true}
											largeGutters={true}
											className={styles.productFeatures_Row}
										>
											{block.banner_with_feature.feature_content.map((content, index) => (
												<Column
													key={index}
													xs={12}
													md={4}
												>
													{content.heading && <h3>{content.heading}</h3>}
													{content.description && (
														<div
															dangerouslySetInnerHTML={{
																__html: content.description,
															}}
														/>
													)}
												</Column>
											))}
										</Row>
									)}
								</Container>
							</div>
						);
					}

					if (block.cards != null) {
						return (
							<CardSection
								data={block.cards}
								key={index}
							/>
						);
					}

					if (block.cta_ribbon?.cta_ribbon?.[0]) {
						return (
							<Container
								width={1200}
								key={index}
								className='my-8 md:my-10'
							>
								<CTARibbon data={block.cta_ribbon.cta_ribbon[0]} />
							</Container>
						);
					}
				})}
		</Layout>
	);
};

PLLP.propTypes = {
	data: PropTypes.object,
	location: PropTypes.object,
};

export default PLLP;

// eslint-disable-next-line react/prop-types -- fix this
export const Head = ({ data }) => (
	<SEO
		// eslint-disable-next-line react/prop-types -- fix this
		{...data.contentstackPllp.seo}
	/>
);

export const query = graphql`
	query ($uid: String!) {
		contentstackPllp(id: { eq: $uid }) {
			url
			ratings_code {
				ratings_code
			}
			breadcrumbs {
				active_page
				back_pages {
					...CTA
				}
			}
			material
			page_hero {
				...NewHero
			}
			modular_blocks {
				all_dynamic {
					heading
					dynamic {
						...Dynamic
					}
				}
				banner {
					banner {
						...ImageCallout
					}
				}
				feature {
					heading
					feature: features {
						...Feature
					}
				}
				performance {
					performance_or_packages {
						...Performance
					}
				}
				reviews {
					heading
					ctas {
						...CTA
					}
				}
				banner_with_feature {
					heading
					image {
						...Image
					}
					feature_content {
						heading
						description
					}
				}
				cards {
					...CardSection
				}
				cta_ribbon {
					cta_ribbon {
						...CTARibbon
					}
				}
			}
			seo {
				title
				description
				noIndex: no_index_page
				page_type
			}
		}
	}
`;
